import { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import { ReceiptView } from '../../../domain/Receipt/ReceiptView'
import ReceiptDetail from "../../../domain/Receipt/ReceiptDetail"
import PreviewReceiptUseCase from '../../../useCase/Receipt/PreviewReceiptUseCase'
import FetchReceiptDetailGateway from '../../../gateway/Receipt/FetchReceiptDetailGateway'
import { downloadFile } from '../../util/DownloadFile'
import { IPreviewFile, PreviewFile } from "../modal/PreviewFile"
import PreviewDocumentModal from "../modal/PreviewModal"
import ErrorModal from "../modal/ErrorModal"
import MimeTypeFactory from "../svg/mimetype/MimeTypeFactory"
import IconFactory from "../svg/IconFactory"
import Spinner from "../svg/Spinner"
import Ban from "../svg/Ban"
import Undo from "../svg/Undo"
import AlertCircle from "../svg/AlertCirle"
import InvalidElement from "../../../domain/Folder/InvalidElementInterface"
import { EFolderStatus } from "../../../domain/Folder/EFolderStatus"
import { useFolderId } from "../../../infrastructure/hooks"

interface Props {
  receipt: ReceiptView
  state: string | null
  invalidElement: InvalidElement|null
  onInvalidateToggle: (
    isInvalid: boolean,
    elementId: string,
    comment: string,
    blockId: string,
    receiptId: string | null,
    value: string | null,
    pageId: string,
  ) => void
  onCommentChange: (elementId: string, receiptId: string, comment: string) => void
}

const Receipt = ({ receipt, state, invalidElement, onInvalidateToggle, onCommentChange }: Props): JSX.Element => {
  const { t } = useTranslation()
  const { folderId } = useFolderId()
  const [isInvalid, setIsInvalid] = useState(false);
  const [comment, setComment] = useState('');
  const [previewReceipt, setPreviewReceipt] = useState<boolean>(false)
  const [errorMessage, setErrorMessage] = useState<string | null>('')
  const [isLoading, setLoading] = useState<boolean>(false)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (invalidElement) {
      setIsInvalid(true)
      setComment(invalidElement.comment)
    }
  }, [invalidElement])

  const toggleInvalid = () => {
    const newInvalidState = !isInvalid;
    setIsInvalid(newInvalidState);

    const initialComment = newInvalidState ? comment : "";
    onInvalidateToggle(newInvalidState, receipt.elementId, initialComment, receipt.blockId, receipt.id, null, receipt.pageId);

    if (!newInvalidState) {
      setComment('');
    } else if (inputRef.current) {
      setTimeout(() => inputRef.current?.focus())
    }
  };

  const handleInputCommentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newComment = e.target.value;
    setComment(newComment);

    if (isInvalid) {
      onCommentChange(receipt.elementId, receipt.id, newComment);
    }
  }

  const getReceiptFile = (receiptId: string): Promise<ReceiptDetail | null> => {
    return new PreviewReceiptUseCase(new FetchReceiptDetailGateway()).execute(receiptId, folderId ?? undefined).then(response => {
      return response
    })
  }

  const downloadReceipt = (receiptToShow: ReceiptView) => () => {
    setLoading(true)
    getReceiptFile(receiptToShow.id).then(response => {
      if (response) {
        downloadFile(response, response.resource)
        setLoading(false)
      } else {
        setLoading(false)
        setErrorMessage(t('ged.download_failed'))
      }
    }).catch((error) => {
      setLoading(false)
      if (error.errors?.length && error.errors[0]?.message?.length > 0) {
        setErrorMessage(t(error.errors[0].message))
      } else {
        setErrorMessage(t('ged.download_failed'))
      }
      return null;
    })
  }

  const fetchReceiptFile = (receiptId: string): Promise<IPreviewFile | null> => {
    return getReceiptFile(receiptId).then(response => {
      if (response) {
        return new PreviewFile(
          response.resource,
          response.title,
          response.mimeType
        )
      } else {
        setErrorMessage(t('ged.download_failed'))
        return null
      }
    }).catch((error) => {
      setLoading(false)
      if (error.errors?.length && error.errors[0]?.message?.length > 0) {
        setErrorMessage(t(error.errors[0].message))
      } else {
        setErrorMessage(t('ged.download_failed'))
      }
      return null;
    })
  }

  const rowClasses = `custom-doc-row u-pas u-mas ${isInvalid ? 'invalid' : ''}`;
  const buttonBaseClasses = 'flex items-center gap-1 u-pxs u-pys rounded text-xs font-medium transition-all';
  const buttonDynamicClasses = isInvalid
    ? 'btn-active-state'
    : 'btn-default-state';

  const commentSectionClasses = `comment-section ml-9 ${isInvalid ? 'open' : ''}`;
  const buttonClassName = isLoading || previewReceipt ? "button-disabled" : "button-enabled"

  return (
    <div id={`row-${receipt.id}`} className={rowClasses}>
      {errorMessage && <ErrorModal message={errorMessage} onClose={() => setErrorMessage(null)} />}
      {previewReceipt && <PreviewDocumentModal id={receipt.id} download={fetchReceiptFile} hide={() => setPreviewReceipt(false)} />}
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-3 overflow-hidden">
          <MimeTypeFactory filename={receipt.title} />
          <div className="shrink-0 text-red-500">
            <i data-lucide="file-text"></i>
          </div>
          <span id={`name-${receipt.id}`} className='font-medium truncate transition-all'>
            {receipt.title}
          </span>
        </div>

        <div className="flex items-center shrink-0 ml-4">
          <div className='validation__cta'>
            <div className="pj-container-min-preview pj-figure" title={previewReceipt ? t('button.view-ongoing') : t('button.view')}>
              <div className={`button align-items-end button--small pj-file-min-preview ${buttonClassName}`}
                onClick={() => setPreviewReceipt(true)}>
                {previewReceipt ? <Spinner /> : <IconFactory type={"eye"} className={'pj-preview'} />}
              </div>
            </div>
          </div>
          <div className='validation__cta'>
            <div className="pj-container-min-preview pj-figure" title={isLoading ? t('button.download-ongoing') : t('button.download')}>
              <div className={`button align-items-end button--small pj-file-min-preview ${buttonClassName}`}
                onClick={downloadReceipt(receipt)}
              >
                {isLoading ? <Spinner /> : <IconFactory type={"download"} className={'pj-preview-fill'} />}
              </div>
            </div>
          </div>

          {(EFolderStatus.Ongoing === state || EFolderStatus.Error === state || EFolderStatus.Pre_signature_validation === state) &&
            <a id={`btn-${receipt.id}`} className={`button ${buttonBaseClasses} ${buttonDynamicClasses}`}
              onClick={toggleInvalid}
            >
              {isInvalid ? (
                <>
                  <Undo color='var(--error)' />
                  <span>{t('button.restore')}</span>
                </>
              ) : (
                <>
                  <Ban color='#94a3b8' />
                  <span>{t('button.invalid')}</span>
                </>
              )}
            </a>
          }
        </div>
      </div>

      {/* Section de commentaire */}
      <div id={`comment-box-${receipt.id}`} className={commentSectionClasses}>
        <div className="relative mt-2">
          <div className="u-absolute inset-y-0 left-0 flex items-center u-pxs pointer-events-none">
            <AlertCircle />
          </div>
          <input
            type="text"
            id={`input-${receipt.id}`}
            placeholder={t('validation.placeholder-error-input')}
            className="block w-full pl-9 pr-3 py-2 sm:text-sm custom-input-error bg-white text-slate-800 placeholder-slate-400 border-red-200"
            value={comment}
            onChange={handleInputCommentChange}
            disabled={!isInvalid}
            ref={inputRef}
          />
        </div>
      </div>
    </div>
  );
};

export default Receipt;